import * as React from 'react';
import { useField } from 'formik';
import { BootstrapSelect } from './SelectField';
import {
  ClickAwayListener,
  FormControl,
  FormControlProps,
  FormHelperText,
  FormHelperTextProps,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  ListItem,
  MenuItem,
  SelectChangeEvent,
  SelectProps,
  Typography,
} from '@mui/material';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import {
  getWCAGAutoCompleteValue,
  sortOptionsInAlphabeticOrder,
} from '../../utils/utilityFunctions';

export interface IKeyValuePair {
  key: any;
  value: string;
}

interface IBaseProps {
  label?: string;
  searchFieldPlaceholder?: string;
  removeSelectionText?: string;
  helperText?: string;
  formControlProps?: FormControlProps;
  formHelperTextProps?: FormHelperTextProps;
  maxVisibleOptions?: number;
  showAll?: boolean;
  noRemoveSelectionOption?: boolean;
}

interface IDefaultKeyValuePair extends IBaseProps {
  options: IKeyValuePair[];
}

interface ICustomKeyValuePair extends IBaseProps {
  keyPropFn: (option: IKeyValuePair | any) => any;
  valuePropFn: (option: IKeyValuePair | any) => string | number;
  options: any[];
}

export type SearchableSelectProps = (IDefaultKeyValuePair | ICustomKeyValuePair) & SelectProps;

interface IClickAwayListenerWrapperProps {
  searchFieldPlaceholder: string | undefined;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
}

export function SearchFieldWrapper({
  searchFieldPlaceholder,
  setQuery,
}: IClickAwayListenerWrapperProps & {
  searchFieldPlaceholder: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element {
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ClickAwayListener onClickAway={() => null}>
      <ListItem sx={{ p: 0 }}>
        <Input
          id="search-input-field"
          inputRef={searchInputRef}
          placeholder={searchFieldPlaceholder || 'Search...'}
          autoComplete="off"
          fullWidth
          inputProps={{
            'aria-label': searchFieldPlaceholder || 'Search options',
          }}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === ' ') {
              e.stopPropagation();
            } else if (e.key === 'ArrowDown' || e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();

              const currentListItem = searchInputRef.current?.closest('li');
              const firstOption = currentListItem?.parentElement?.querySelector(
                'li[role="option"]'
              ) as HTMLElement;

              if (firstOption) {
                firstOption.focus();
              }
            } else if (e.key === 'ArrowUp') {
              e.stopPropagation();
            }
          }}
          startAdornment={
            <InputAdornment position="start">
              <IconButton tabIndex={-1} type="button" aria-label="Search">
                <SearchSharpIcon sx={{ color: 'primary.main' }} />
              </IconButton>
            </InputAdornment>
          }
          sx={{ mx: 2, my: 1 }}
        />
      </ListItem>
    </ClickAwayListener>
  );
}

export function renderFilteredOptions(
  items: { key: string | number; value: string | number }[],
  query: string
): any {
  const filteredOptions =
    items &&
    items.filter &&
    items.filter((item: IKeyValuePair | any) => {
      return (
        !item.key ||
        (item.key && item.key.toString().toLowerCase().indexOf(query.toLowerCase()) !== -1)
      );
    });

  return filteredOptions?.length > 0 ? (
    sortOptionsInAlphabeticOrder(filteredOptions || []).map(
      (
        option:
          | {
              key: string | number;
              value: string | number;
            }
          | any
      ) => {
        const optionId = `opt-${option.value}`;
        return (
          <MenuItem
            key={option.key}
            value={option.value}
            id={optionId}
            role="option"
            aria-labelledby={optionId}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: 'common.black',
              '&.Mui-selected,&.Mui-selected:hover': { backgroundColor: 'unset' },
            }}>
            <Typography
              id={optionId}
              sx={{ fontSize: 14, whiteSpace: { xs: 'pre-wrap', sm: 'nowrap' } }}
              component="span">
              {option.key}
            </Typography>
          </MenuItem>
        );
      }
    )
  ) : (
    <Typography
      component="h2"
      role="status"
      aria-live="polite"
      sx={{
        paddingTop: '6px',
        paddingBottom: '6px',
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        paddingLeft: '16px',
        paddingRight: '16px',
        fontSize: '14px',
        fontWeight: 500,
      }}
      ref={(node: HTMLDivElement | null) => {
        if (node) {
          setTimeout(() => {
            node.textContent = 'No results found';
          }, 0);
        }
      }}>
      No Results found
    </Typography>
  );
}

export function SearchableSelect({
  name,
  label,
  items,
  onChange,
  disabled = false,
  searchFieldPlaceholder = '',
  applyRenderValue = false,
  ...props
}: SelectProps & {
  name: string;
  label?: string;
  items: { key: string | number; value: string | number }[];
  onChange?: (event: SelectChangeEvent<unknown>) => void;
  disabled?: boolean;
  searchFieldPlaceholder?: string;
  applyRenderValue?: boolean;
}): JSX.Element {
  const [query, setQuery] = React.useState('');
  const [field, meta] = useField(name);
  onChange = onChange || field.onChange;
  const errorText = meta.error && meta.touched ? meta.error : '';

  const { placeholder, ...cleanProps } = props;

  const selectId = `${name}-searchable-select`;
  const labelId = `${name}-searchable-label`;
  const errorId = `${name}-searchable-error`;
  const autoCompleteToken = getWCAGAutoCompleteValue(name || '');

  /* FIX: Detects if the incoming string contains an asterisk element */
  const containsAsterisk = label ? label.includes('*') : false;
  return (
    <FormControl fullWidth sx={{ my: { xs: 1, sm: 1, md: 2 } }}>
      {label && (
        <InputLabel
          shrink
          id={labelId}
          htmlFor={selectId}
          sx={{ transform: 'unset', fontSize: 14, fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}>
          {label}
        </InputLabel>
      )}
      <BootstrapSelect
        id={selectId}
        labelId={labelId}
        aria-invalid={!!errorText}
        inputProps={{
          id: selectId,
          ...(autoCompleteToken ? { autoComplete: autoCompleteToken } : {}),
          ...(containsAsterisk && {
            'aria-label': `${label?.replace('*', '').trim()} required`,
            'aria-required': true,
          }),
        }}
        sx={{
          mt: '32px',
          ...cleanProps.sx,
          '& .Mui-disabled': {
            cursor: !disabled ? 'pointer' : 'not-allowed !important',
            pointerEvents: 'all',
          },
          '&.Mui-focusVisible .MuiSelect-select': {
            border: '2px solid #2057A6',
            borderRadius: '4px',
            padding: '9px 11px',
          },
        }}
        MenuProps={{
          sx: {
            '.MuiPaper-root ': {
              maxWidth: { xs: '100%', sm: '31%', md: '26%' },
              maxHeight: '20%',
              overflowX: 'auto',
            },
          },
          // FIX: Overrides Material UI menu auto-focus trapping to make the search field reachable
          disableAutoFocusItem: true,
          MenuListProps: {
            disableListWrap: true,
          },
          // FIX: Disables structural popover focus capture locks to allow standard tab looping inside the popup box
          PopoverClasses: {
            root: 'mui-popover-accessible-trap',
          },
        }}
        onClose={() => {
          setQuery('');
        }}
        {...field}
        onChange={onChange}
        disabled={disabled}
        renderValue={(Selectedvalue) => {
          const selectedKey = items.find((_item) => _item.value == Selectedvalue);
          return (
            <Typography
              sx={{ fontSize: 14, whiteSpace: { xs: 'pre-wrap', sm: 'nowrap' } }}
              component="span">
              {applyRenderValue
                ? (selectedKey?.key as unknown as string)?.split('- ')[1]
                : (selectedKey?.key as unknown as string)}
            </Typography>
          );
        }}
        {...cleanProps}>
        <SearchFieldWrapper searchFieldPlaceholder={searchFieldPlaceholder} setQuery={setQuery} />
        {renderFilteredOptions(items, query)}
      </BootstrapSelect>
      {errorText && (
        <FormHelperText
          error
          id={errorId}
          role="alert"
          aria-live="assertive"
          sx={{ marginLeft: 'unset' }}>
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
